// Phase 1.9.19 — Entry Cover (invitation-level "tap to open" overlay).
//
// Strictly additive: this component lives outside the canvas, outside
// element arrays, outside layout resolution. It mounts above the opener
// (z 10010 vs opener's 9999) and owns the single guest gesture when
// enabled. After fade-out it unmounts entirely.
//
// One-tap coordination is owned by the parent (PublicInvite). On click,
// the parent's onTap handler:
//   1) calls invitationAudio.play() synchronously inside the gesture
//   2) fires the opener's startRef so timeline begins immediately
//   3) starts the cover fade
// — therefore this component only needs to:
//   • render the visual
//   • bubble exactly one tap to onTap
//   • show a 'leaving' fade when prop `leaving` flips true
//
// Failure modes (per spec §14):
//   • bg image fails → background color shows through (img onError hides itself)
//   • identity image fails → hidden, label/cta still usable
//   • audio fails → parent handles, this component doesn't block

import React, { useEffect, useState } from 'react';

const SIZE_MAP = { small: 120, medium: 180, large: 240 };

const API = process.env.REACT_APP_BACKEND_URL;
function abs(url) {
  if (!url) return '';
  if (url.startsWith('/')) return `${API}${url}`;
  return url;
}

let _injected = false;
function injectStyles() {
  if (_injected || typeof document === 'undefined') return;
  _injected = true;
  const style = document.createElement('style');
  style.setAttribute('data-entry-cover', '1');
  style.textContent = `
    @keyframes ec-cue-pulse {
      0%   { opacity: 0.35; transform: scale(0.92); }
      50%  { opacity: 0.85; transform: scale(1.06); }
      100% { opacity: 0.35; transform: scale(0.92); }
    }
    @keyframes ec-cue-fade-in {
      from { opacity: 0; transform: translate(-50%, 6px); }
      to   { opacity: 1; transform: translate(-50%, 0); }
    }
    .entry-cover {
      position: fixed;
      inset: 0;
      z-index: 10010;
      min-height: 100svh;
      min-height: 100dvh;
      overflow: hidden;
      padding-top: env(safe-area-inset-top);
      padding-bottom: env(safe-area-inset-bottom);
      padding-left: env(safe-area-inset-left);
      padding-right: env(safe-area-inset-right);
      cursor: pointer;
      opacity: 1;
      transition: opacity 500ms ease;
    }
    .entry-cover--leaving { opacity: 0; pointer-events: none; }
    .entry-cover__bg-image {
      position: absolute; inset: 0; width: 100%; height: 100%;
      object-fit: cover; user-select: none; pointer-events: none;
    }
    /* Identity image — Phase 1.9.20: full-viewport-width centering via
       absolute positioning + flex. Independent of any content wrapper
       width so the image always sits on the true visual centre.        */
    .entry-cover__identity-wrap {
      position: absolute;
      inset: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      pointer-events: none;
    }
    .entry-cover__identity {
      max-width: 60vw;
      max-height: 30vh;
      width: auto;
      height: auto;
      object-fit: contain;
      user-select: none;
      display: block;
    }
    /* Optional headline text — only renders when configured. Sits a
       little above the bottom cue, still centred against viewport.    */
    .entry-cover__text {
      position: absolute;
      left: 50%;
      bottom: calc(120px + env(safe-area-inset-bottom));
      transform: translateX(-50%);
      margin: 0;
      max-width: 32ch;
      text-align: center;
      font-family: 'Cormorant Garamond', Georgia, serif;
      font-size: 1.05rem;
      line-height: 1.5;
      letter-spacing: 0.02em;
      pointer-events: none;
    }
    /* Bottom cue — subtle pulse + "Tap to open" caption.
       Restrained typography, no button container, no heavy overlay. */
    .entry-cover__cue {
      position: absolute;
      left: 50%;
      bottom: calc(48px + env(safe-area-inset-bottom));
      transform: translateX(-50%);
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 10px;
      pointer-events: none;
      animation: ec-cue-fade-in 700ms ease 400ms both;
    }
    .entry-cover__cue-glyph {
      width: 18px;
      height: 18px;
      border-radius: 50%;
      animation: ec-cue-pulse 2.2s ease-in-out infinite;
    }
    .entry-cover__cue-caption {
      font-size: 10px;
      letter-spacing: 0.28em;
      text-transform: uppercase;
      font-family: 'Cormorant Garamond', Georgia, serif;
      opacity: 0.75;
    }
    @media (prefers-reduced-motion: reduce) {
      .entry-cover { transition: opacity 250ms ease; }
      .entry-cover__cue { animation: none; }
      .entry-cover__cue-glyph { animation: none; opacity: 0.7; }
    }
  `;
  document.head.appendChild(style);
}

/**
 * @param {Object} props
 * @param {Object} props.config              Entry-cover settings (see backend EntryCoverUpdate).
 * @param {() => void | Promise<void>} props.onTap
 *   Called once on the first tap. Parent decides what happens (audio + opener + fade).
 * @param {boolean} [props.leaving]          Toggle to start the fade-out.
 */
export default function EntryCover({ config = {}, onTap, leaving = false }) {
  useEffect(() => { injectStyles(); }, []);
  const [bgImgFailed, setBgImgFailed] = useState(false);
  const [idImgFailed, setIdImgFailed] = useState(false);
  const [tapped, setTapped] = useState(false);

  const {
    backgroundType = 'color',
    backgroundColor = '#F5F0E8',
    backgroundImageUrl = '',
    identityImageUrl = '',
    identityImageSize = 'medium',
    text = '',
    label = 'Open invitation',
    textColor = '#1F1F1F',
    accentColor = '#1F1F1F',
  } = config;

  const showBgImage = backgroundType === 'image' && backgroundImageUrl && !bgImgFailed;
  const sizePx = SIZE_MAP[identityImageSize] || SIZE_MAP.medium;

  const handleTap = async (e) => {
    e.preventDefault();
    if (tapped) return;
    setTapped(true);
    try {
      await onTap?.();
    } catch (_) { /* parent handles errors */ }
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleTap(e);
    }
  };

  return (
    <div
      className={leaving ? 'entry-cover entry-cover--leaving' : 'entry-cover'}
      style={{ backgroundColor }}
      onClick={handleTap}
      onKeyDown={handleKey}
      role="button"
      tabIndex={0}
      aria-label={label || 'Open invitation'}
      data-testid="entry-cover"
    >
      {showBgImage && (
        <img
          className="entry-cover__bg-image"
          src={abs(backgroundImageUrl)}
          alt=""
          draggable={false}
          onError={() => setBgImgFailed(true)}
        />
      )}
      {/* Identity image — Phase 1.9.20: centered against the full
          viewport via an absolute flex wrapper, not against any
          narrower inner content box. */}
      {identityImageUrl && !idImgFailed && (
        <div className="entry-cover__identity-wrap">
          <img
            className="entry-cover__identity"
            src={abs(identityImageUrl)}
            alt=""
            style={{ width: `min(60vw, ${sizePx}px)` }}
            draggable={false}
            onError={() => setIdImgFailed(true)}
            data-testid="entry-cover-identity"
          />
        </div>
      )}
      {/* Optional headline */}
      {text && (
        <p className="entry-cover__text" style={{ color: textColor }}>{text}</p>
      )}
      {/* Bottom cue — subtle pulse + "Tap to open" caption. The whole
          cover is the tap target so this is purely a visual cue, not a
          button. Caption is intentionally literal "Tap to open" per
          spec; the saved `label` field still drives the accessible
          aria-label of the tap target above. */}
      <div className="entry-cover__cue" aria-hidden="true" data-testid="entry-cover-cue">
        <span
          className="entry-cover__cue-glyph"
          style={{ backgroundColor: accentColor }}
        />
        <span
          className="entry-cover__cue-caption"
          style={{ color: accentColor }}
        >
          Tap to open
        </span>
      </div>
    </div>
  );
}

// Default config — used by the Studio editor and as a fallback when a
// saved event has an entry_cover object with missing fields. Kept here
// so the public + Studio surfaces agree on defaults.
export const ENTRY_COVER_DEFAULTS = {
  enabled: false,
  backgroundType: 'color',
  backgroundColor: '#F5F0E8',
  backgroundImageUrl: '',
  identityImageUrl: '',
  identityImageSize: 'medium',
  text: '',
  label: 'Open invitation',
  textColor: '#1F1F1F',
  accentColor: '#1F1F1F',
  transition: 'fade',
};
